import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Share2, Download } from 'lucide-react';
import StarRating from '../shared/StarRating';
import ShareModal from '../modals/ShareModal';

export default function VehicleLightbox({ images, initialIndex, onClose, onRatingChange }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    // Speichere aktuelle Scroll-Position
    const scrollY = window.scrollY;
    
    // Sperre Body-Scroll komplett wenn Lightbox offen
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose(currentIndex); // Pass currentIndex beim Schließen
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowUp') handlePrevious();
      if (e.key === 'ArrowDown') handleNext();
    };
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Entsperre UND stelle Scroll-Position wieder her!
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [currentIndex]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Touch Handlers für Vertikal-Swipe
  const handleTouchStart = (e) => {
    if (e.targetTouches && e.targetTouches[0]) {
      setTouchStart(e.targetTouches[0].clientY);
    }
  };

  const handleTouchMove = (e) => {
    if (e.targetTouches && e.targetTouches[0]) {
      setTouchEnd(e.targetTouches[0].clientY);
    }
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isSwipeUp = distance > 30;  // Empfindlicher: 30px statt 50px
    const isSwipeDown = distance < -30;

    if (isSwipeUp) {
      handleNext(); // Nach oben swipen = Nächstes Bild
    }
    if (isSwipeDown) {
      handlePrevious(); // Nach unten swipen = Vorheriges Bild
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const currentImage = images[currentIndex];

  return (
    <div 
      className="fixed inset-0 z-[90] bg-black flex flex-col"
      onClick={() => onClose(currentIndex)}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <div className="text-white/60 text-sm">
          {currentIndex + 1} / {images.length}
        </div>
        <div className="flex items-center gap-2">
          {/* Download Button - HIDDEN ON MOBILE */}
          <a
            href={currentImage.url}
            download={`snapshot-${currentIndex + 1}.jpg`}
            onClick={(e) => e.stopPropagation()}
            className="hidden md:flex w-10 h-10 items-center justify-center rounded-full border-2 border-white/30 text-white hover:bg-blue-500 hover:border-blue-500 transition-all"
          >
            <Download className="w-5 h-5" />
          </a>
          {/* Share Button - HIDDEN ON MOBILE */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowShareModal(true);
            }}
            className="hidden md:flex w-10 h-10 items-center justify-center rounded-full border-2 border-white/30 text-white hover:bg-[#10B981] hover:border-[#10B981] hover:text-black transition-all"
          >
            <Share2 className="w-5 h-5" />
          </button>
          {/* Close Button */}
          <button
            onClick={() => onClose(currentIndex)}
            className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-white/30 text-white hover:bg-white/10 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Image Area */}
      <div 
        className="flex-1 flex items-center justify-center px-4 sm:px-0 relative" 
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        
        {/* Previous Button - nur Desktop */}
        {images.length > 1 && (
          <button
            onClick={handlePrevious}
            className="hidden sm:flex absolute left-4 w-12 h-12 items-center justify-center rounded-full bg-black/80 border-2 border-white/30 text-white hover:bg-white/10 transition-all z-10"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {/* Image Container */}
        <div className="relative w-full h-full sm:h-auto flex items-center justify-center">
          {/* Image - Object-contain damit ganzes Auto sichtbar */}
          <img 
            src={currentImage.url} 
            alt={`Image ${currentIndex + 1}`}
            className="max-w-full max-h-[85vh] object-contain"
          />
          
          {/* Sterne + Share-Button IM Bild unten - nur Mobile */}
          <div className="sm:hidden absolute bottom-4 left-0 right-0 flex justify-center gap-3">
            <div className="bg-black/90 backdrop-blur-sm px-4 py-3 rounded-full border border-white/20">
              <StarRating 
                imageId={currentImage.id} 
                initialRating={currentImage.rating || 0} 
                onChange={onRatingChange}
              />
            </div>
            
            {/* Share Button - öffnet ShareModal */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowShareModal(true);
              }}
              className="bg-black/90 backdrop-blur-sm px-5 py-3 rounded-full border border-white/20 text-white hover:bg-white/10 transition-all flex items-center gap-2"
              aria-label="Bild teilen"
            >
              <Share2 className="w-4 h-4" />
              <span className="text-sm font-medium">Share</span>
            </button>
          </div>
        </div>

        {/* Next Button - nur Desktop */}
        {images.length > 1 && (
          <button
            onClick={handleNext}
            className="hidden sm:flex absolute right-4 w-12 h-12 items-center justify-center rounded-full bg-black/80 border-2 border-white/30 text-white hover:bg-white/10 transition-all z-10"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Bottom Section - Nur Desktop */}
      <div className="hidden sm:block p-6 bg-black/95 border-t border-white/10" onClick={(e) => e.stopPropagation()}>
        {/* Prompt */}
        {currentImage.prompt && (
          <p className="text-white/60 text-sm text-center font-mono max-w-2xl">
            {currentImage.prompt}
          </p>
        )}
        
        {/* Star Rating */}
        <div className="flex items-center gap-3">
          <span className="text-white/60 text-sm">Bewertung:</span>
          <StarRating 
            imageId={currentImage.id} 
            initialRating={currentImage.rating || 0}
            onChange={onRatingChange}
          />
        </div>
      </div>
      
      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          imageUrl={currentImage.url}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
}
